import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  BookOpen,
  Plus,
  Search,
  Trash2,
  ArrowLeft,
  X,
  Target,
  CheckCircle,
  XCircle,
  Hash,
  Zap,
  AlertTriangle
} from 'lucide-react';

interface Question {
  id: number;
  subject: string;
  topic: string;
  difficulty: string;
  questionText: string;
  questionImage?: string;
  options: {
    A: string;
    B: string;
    C: string;
    D: string;
  };
  correctAnswer: string;
  explanation?: string;
  explanationImage?: string;
  marks: number;
  negativeMarks: number;
  year?: number;
  source?: string;
  tags: string[];
  order?: number;
}

interface Test {
  id: number;
  title: string;
  totalQuestions: number;
  subjects: string[];
  difficulty: string;
}

export default function TestQuestions() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  
  const [test, setTest] = useState<Test | null>(null);
  const [testQuestions, setTestQuestions] = useState<Question[]>([]);
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, sets] = useState({
    subject: 'all',
    difficulty: 'all',
    topic: 'all'
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAutoGenerateModal, setShowAutoGenerateModal] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [currentPage, _setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);

  const [autoGenerateData, setAutoGenerateData] = useState({
    mathQuestions: 25,
    physicsQuestions: 25,
    chemistryQuestions: 25,
    difficulty: 'medium',
    includeYears: [] as number[],
    excludeTopics: [] as string[]
  });

  useEffect(() => {
    if (testId) {
      fetchTestDetails();
      fetchTestQuestions();
    }
  }, [testId]);

  useEffect(() => {
    if (showAddModal) {
      fetchAvailableQuestions();
    }
  }, [showAddModal, currentPage, searchTerm, filters]);

  const fetchTestDetails = async () => {
    try {
      const response = await adminAPI.getTestDetails(testId!);
      if (response.data.success) {
        setTest(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load test details');
      navigate('/admin/tests');
    }
  };

  const fetchTestQuestions = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTestQuestions(testId!);
      if (response.data.success) {
        setTestQuestions(response.data.data.questions);
      }
    } catch (error: any) {
      toast.error('Failed to load test questions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableQuestions = async () => {
    try {
      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm,
        ...(filters.subject !== 'all' && { subject: filters.subject }),
        ...(filters.difficulty !== 'all' && { difficulty: filters.difficulty }),
        ...(filters.topic !== 'all' && { topic: filters.topic }),
        isActive: 'true'
      };

      const response = await adminAPI.getQuestions(params);
      if (response.data.success) {
        //out questions already in the test
        const testQuestionIds = testQuestions.map(q => q.id);
        const filtered = response.data.data.questions.filter(
          (q: Question) => !testQuestionIds.includes(q.id)
        );
        setAvailableQuestions(filtered);
        setTotalPages(response.data.data.pagination.pages);
      }
    } catch (error) {
      toast.error('Failed to load available questions');
    }
  };

  const handleAddQuestions = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('Please select questions to add');
      return;
    }

    try {
      const response = await adminAPI.addQuestionsToTest(testId!, selectedQuestions);
      if (response.data.success) {
        toast.success(`Added ${selectedQuestions.length} questions to test`);
        setShowAddModal(false);
        setSelectedQuestions([]);
        fetchTestQuestions();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to add questions');
    }
  };

  const handleRemoveQuestion = async (questionId: number) => {
    if (!confirm('Are you sure you want to remove this question from the test?')) {
      return;
    }

    try {
      await adminAPI.removeQuestionsFromTest(testId!, [questionId]);
      toast.success('Question removed from test');
      fetchTestQuestions();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to remove question');
    }
  };

  const handleAutoGenerate = async () => {
    if (!confirm('This will replace all existing questions in the test. Are you sure?')) {
      return;
    }

    try {
      const response = await adminAPI.autoGenerateQuestions(testId!, autoGenerateData);
      if (response.data.success) {
        toast.success(`Auto-generated ${response.data.data.totalQuestions} questions`);
        setShowAutoGenerateModal(false);
        fetchTestQuestions();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to auto-generate questions');
    }
  };

  const toggleQuestionSelection = (questionId: number) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'danger';
      default: return 'secondary';
    }
  };

  const getSubjectColor = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'mathematics': return 'primary';
      case 'physics': return 'success';
      case 'chemistry': return 'warning';
      default: return 'secondary';
    }
  };

  const filteredAvailableQuestions = availableQuestions.filter(question => {
    const matchesSearch = question.questionText.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.topic.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = filters.subject === 'all' || question.subject === filters.subject;
    const matchesDifficulty = filters.difficulty === 'all' || question.difficulty === filters.difficulty;
    const matchesTopic = filters.topic === 'all' || question.topic.includes(filters.topic);
    
    return matchesSearch && matchesSubject && matchesDifficulty && matchesTopic;
  });

  const getSubjectStats = () => {
    const stats = {
      mathematics: testQuestions.filter(q => q.subject === 'mathematics').length,
      physics: testQuestions.filter(q => q.subject === 'physics').length,
      chemistry: testQuestions.filter(q => q.subject === 'chemistry').length
    };
    return stats;
  };

  if (!test) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading test details...</p>
        </div>
      </div>
    );
  }

  const subjectStats = getSubjectStats();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/tests')}
              className="p-2 text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-secondary-900 mb-2">
                {test.title} - Questions
              </h1>
              <p className="text-secondary-600">
                Manage questions for this test ({testQuestions.length}/{test.totalQuestions} questions)
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowAutoGenerateModal(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-accent-600 border border-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
            >
              <Zap className="h-4 w-4 mr-2" />
              Auto Generate
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Questions
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{testQuestions.length}</div>
          <div className="stats-label">Total Questions</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <Hash className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{subjectStats.mathematics}</div>
          <div className="stats-label">Mathematics</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-success-500 to-success-600 rounded-xl flex items-center justify-center">
              <Hash className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{subjectStats.physics}</div>
          <div className="stats-label">Physics</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-warning-500 to-warning-600 rounded-xl flex items-center justify-center">
              <Hash className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{subjectStats.chemistry}</div>
          <div className="stats-label">Chemistry</div>
        </div>
      </div>

      {/* Test Questions List */}
      <div className="card mb-6">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-secondary-900">Test Questions</h3>
          <p className="text-secondary-600">Questions currently in this test</p>
        </div>
        <div className="card-content">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-secondary-600">Loading questions...</p>
            </div>
          ) : testQuestions.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-secondary-900 mb-2">No Questions Added</h4>
              <p className="text-secondary-600 mb-4">
                This test doesn't have any questions yet. Add some questions to get started.
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors mx-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Questions
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {testQuestions.map((question, index) => (
                <div key={question.id} className="border border-secondary-200 rounded-lg p-4 hover:bg-secondary-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-sm font-medium text-secondary-500">
                          Q{index + 1}
                        </span>
                        <span className={`badge badge-${getSubjectColor(question.subject)}`}>
                          {question.subject.charAt(0).toUpperCase() + question.subject.slice(1)}
                        </span>
                        <span className={`badge badge-${getDifficultyColor(question.difficulty)}`}>
                          {question.difficulty}
                        </span>
                        <span className="badge badge-secondary">
                          {question.topic}
                        </span>
                      </div>
                      
                      <p className="text-secondary-900 leading-relaxed line-clamp-2 mb-2">
                        {question.questionText}
                      </p>

                      <div className="flex items-center space-x-4 text-sm text-secondary-600">
                        <div className="flex items-center">
                          <Target className="h-4 w-4 mr-1" />
                          {question.marks} marks
                        </div>
                        <div className="flex items-center">
                          <XCircle className="h-4 w-4 mr-1" />
                          {question.negativeMarks} negative
                        </div>
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Answer: {question.correctAnswer}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleRemoveQuestion(question.id)}
                        className="p-2 text-secondary-600 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                        title="Remove from test"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Questions Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">Add Questions to Test</h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedQuestions([]);
                  }}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Search ands */}
              <div className="mb-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                    <input
                      type="text"
                      placeholder="Search questions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <select
                    value={filters.subject}
                    onChange={(e) => sets({ ...filters, subject: e.target.value })}
                    className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Subjects</option>
                    <option value="mathematics">Mathematics</option>
                    <option value="physics">Physics</option>
                    <option value="chemistry">Chemistry</option>
                  </select>
                  
                  <select
                    value={filters.difficulty}
                    onChange={(e) => sets({ ...filters, difficulty: e.target.value })}
                    className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Difficulties</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>

                  <div className="text-sm text-secondary-600">
                    {selectedQuestions.length} selected
                  </div>
                </div>
              </div>

              {/* Available Questions */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredAvailableQuestions.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="h-8 w-8 text-secondary-400 mx-auto mb-2" />
                    <p className="text-secondary-600">No questions available</p>
                  </div>
                ) : (
                  filteredAvailableQuestions.map((question) => (
                    <div
                      key={question.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedQuestions.includes(question.id)
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-secondary-200 hover:bg-secondary-50'
                      }`}
                      onClick={() => toggleQuestionSelection(question.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              checked={selectedQuestions.includes(question.id)}
                              onChange={() => toggleQuestionSelection(question.id)}
                              className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className={`badge badge-${getSubjectColor(question.subject)}`}>
                              {question.subject.charAt(0).toUpperCase() + question.subject.slice(1)}
                            </span>
                            <span className={`badge badge-${getDifficultyColor(question.difficulty)}`}>
                              {question.difficulty}
                            </span>
                            <span className="badge badge-secondary">
                              {question.topic}
                            </span>
                          </div>
                          
                          <p className="text-secondary-900 leading-relaxed line-clamp-2">
                            {question.questionText}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary-200">
                <div className="text-sm text-secondary-600">
                  Can add up to {test.totalQuestions - testQuestions.length} more questions
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setSelectedQuestions([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddQuestions}
                    disabled={selectedQuestions.length === 0}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add {selectedQuestions.length} Questions
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto Generate Modal */}
      {showAutoGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">Auto Generate Questions</h3>
                <button
                  onClick={() => setShowAutoGenerateModal(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-warning-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-warning-800">Warning</h4>
                      <p className="text-sm text-warning-700">
                        This will replace all existing questions in the test with auto-generated ones.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Mathematics Questions
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={autoGenerateData.mathQuestions}
                      onChange={(e) => setAutoGenerateData({
                        ...autoGenerateData,
                        mathQuestions: parseInt(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Physics Questions
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={autoGenerateData.physicsQuestions}
                      onChange={(e) => setAutoGenerateData({
                        ...autoGenerateData,
                        physicsQuestions: parseInt(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Chemistry Questions
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={autoGenerateData.chemistryQuestions}
                      onChange={(e) => setAutoGenerateData({
                        ...autoGenerateData,
                        chemistryQuestions: parseInt(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Difficulty Level
                  </label>
                  <select
                    value={autoGenerateData.difficulty}
                    onChange={(e) => setAutoGenerateData({
                      ...autoGenerateData,
                      difficulty: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>

                <div className="text-center">
                  <p className="text-sm text-secondary-600">
                    Total: {autoGenerateData.mathQuestions + autoGenerateData.physicsQuestions + autoGenerateData.chemistryQuestions} questions
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-secondary-200">
                <button
                  onClick={() => setShowAutoGenerateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAutoGenerate}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-accent-600 border border-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Generate Questions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}